import React, { useEffect, useState } from 'react';
import { appointmentService } from '../../services/appointment.service';
import { TimeOffResponseDto } from '../../types/appointment.types';
import {
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Grid,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { toastService } from '../../services/toast.service';

export const TimeOffSettings: React.FC = () => {
  const [timeOffs, setTimeOffs] = useState<TimeOffResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for the new time-off form
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const fetchTimeOffs = async () => {
    try {
      setLoading(true);
      const data = await appointmentService.getTimeOffPeriods();
      setTimeOffs(data);
    } catch (err) {
      setError('Не вдалося завантажити періоди відпусток.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeOffs();
  }, []);

  const handleAdd = async () => {
    if (!startDate || !endDate) {
      toastService.error('Дати початку та закінчення є обов\'язковими.');
      return;
    }
    setIsAdding(true);
    try {
      await appointmentService.createTimeOffPeriod({ startDate, endDate, reason });
      toastService.success('Період відсутності успішно додано.');
      // Reset form and refresh list
      setStartDate('');
      setEndDate('');
      setReason('');
      await fetchTimeOffs();
    } catch (err) {
      toastService.error('Не вдалося додати період.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await appointmentService.deleteTimeOffPeriod(id);
      toastService.success('Період відсутності успішно видалено.');
      await fetchTimeOffs(); // Refresh list
    } catch (err) {
      toastService.error('Не вдалося видалити період.');
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Відпустки / Вихідні
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Form to add new time-off */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Дата початку"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            label="Дата закінчення"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Причина (необов\'язково)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={isAdding}
            fullWidth
            sx={{ height: '100%' }}
          >
            {isAdding ? <CircularProgress size={24} /> : 'Додати'}
          </Button>
        </Grid>
      </Grid>

      {/* List of existing time-offs */}
      <Typography variant="subtitle1" gutterBottom>Список періодів:</Typography>
      {timeOffs.length === 0 ? (
        <Typography>Немає запланованих періодів відсутності.</Typography>
      ) : (
        <List>
          {timeOffs.map((period) => (
            <ListItem
              key={period.id}
              divider
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(period.id)}>
                  <Delete />
                </IconButton>
              }
            >
              <ListItemText
                primary={`${new Date(period.startDate).toLocaleDateString()} - ${new Date(period.endDate).toLocaleDateString()}`}
                secondary={period.reason || 'Без причини'}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};
